import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { Channel } from 'stream-chat';

import { getCurrentLocationUuid } from 'selectors/session';

import { getStreamChatClient } from '../initializeStreamChatClient';

type MessageError = {
  type: 'client_not_initialized' | 'channel_not_found' | 'send_failed';
  message: string;
  originalError?: Error;
};

type ErrorWithType = Error & {
  errorType?: 'client_not_initialized' | 'channel_not_found' | 'send_failed';
};

type UseSendMessageReturn = {
  sendToEntireTeam: (message: string) => Promise<void>;
  isLoading: boolean;
  error: MessageError | null;
  clearError: () => void;
};

/**
 * Custom hook for sending messages to team channels via Stream Chat
 *
 * Features:
 * - Sends messages to entire team channel
 * - Proper error handling and loading states
 * - Memoized functions for performance
 * - TypeScript support
 *
 * @returns {UseSendMessageReturn} Hook utilities for sending messages
 */
export const useSendMessage = (): UseSendMessageReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<MessageError | null>(null);

  const client = getStreamChatClient();
  const teamUUID = useSelector(getCurrentLocationUuid);

  // Finds the entire team channel for the current location

  const getEntireTeamChannel =
    useCallback(async (): Promise<Channel | null> => {
      if (!client) {
        throw new Error('Stream Chat client is not initialized');
      }

      if (!teamUUID) {
        throw new Error('Team UUID is not available');
      }

      try {
        const channels = await client.queryChannels({
          type: 'entire_team',
          members: { $in: [client?.user?.id || ''] },
          team: teamUUID,
        });

        return (
          channels?.filter(channel =>
            channel?.id?.endsWith('entire_team')
          )[0] || null
        );
      } catch (caughtError) {
        throw new Error(
          `Failed to query channels: ${
            caughtError instanceof Error ? caughtError.message : 'Unknown error'
          }`
        );
      }
    }, [client, teamUUID]);

  const sendMessage = useCallback(
    async (message: string, channel: Channel): Promise<void> => {
      // Send the message directly to avoid duplicate loading/error management
      await channel.sendMessage({
        text: message,
      });
    },
    []
  );

  // Sends a message to the entire team channel

  const sendToEntireTeam = useCallback(
    async (message: string): Promise<void> => {
      if (!message.trim()) {
        setError({
          type: 'send_failed',
          message: 'Message cannot be empty',
        });
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Validate client is available
        if (!client) {
          const clientError = new Error(
            'Stream Chat client is not initialized'
          ) as ErrorWithType;
          clientError.errorType = 'client_not_initialized';
          throw clientError;
        }

        // Get the team channel
        const channel = await getEntireTeamChannel();

        if (!channel) {
          const channelError = new Error(
            'Entire team channel not found'
          ) as ErrorWithType;
          channelError.errorType = 'channel_not_found';
          throw channelError;
        }

        await sendMessage(message, channel);
      } catch (caughtError) {
        // Handle all errors consistently
        let sendError: MessageError;

        if (caughtError instanceof Error) {
          const errorWithType = caughtError as ErrorWithType;
          sendError = {
            type: errorWithType.errorType || 'send_failed',
            message: caughtError.message,
            originalError: caughtError,
          };
        } else {
          sendError = {
            type: 'send_failed',
            message: 'Failed to send message to entire team',
          };
        }

        setError(sendError);
        throw sendError;
      } finally {
        setIsLoading(false);
      }
    },
    [client, getEntireTeamChannel, sendMessage]
  );

  /**
   * Clears the current error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    sendToEntireTeam,
    isLoading,
    error,
    clearError,
  };
};
